import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { DecimalPipe } from "@angular/common";
import { IMapStore, IMapMall, IMapPotentialStoreLocation } from "../../_entities";
import { ApiService } from "../../_services";
import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer, Cluster, ClusterStats, GridAlgorithm } from "@googlemaps/markerclusterer";
import { PotentialStoreLocationClient, MallMetadataClient } from "../../_generated/generated-pipe-api";

@Component({
	selector: 'main-map',
	template: require('./main-map.component.html')
})
export class MainMap implements OnInit {

	constructor(private apiService: ApiService, private potentialStoreLocationClient: PotentialStoreLocationClient, private mallMetadataClient: MallMetadataClient) { }

	readonly smallRedSvgMarker = this.createSvgMarker(false, "#ff0000");
	readonly largeRedSvgMarker = this.createSvgMarker(true, "#ff0000");

	readonly smallPinkSvgMarker = this.createSvgMarker(false, "#ff69b4");
	readonly largePinkSvgMarker = this.createSvgMarker(true, "#ff69b4");

	readonly smallPurpleSvgMarker = this.createSvgMarker(false, "#a020f0");
	readonly largePurpleSvgMarker = this.createSvgMarker(true, "#a020f0");

	readonly smallBlueSvgMarker = this.createSvgMarker(false, "#0000ff");
	readonly largeBlueSvgMarker = this.createSvgMarker(true, "#0000ff");

	readonly smallGreenSvgMarker = this.createSvgMarker(false, "#00ff00");
	readonly largeGreenSvgMarker = this.createSvgMarker(true, "#00ff00");

	readonly smallMidGreenSvgMarker = this.createSvgMarker(false, "#00c600");
	readonly largeMidGreenSvgMarker = this.createSvgMarker(true, "#00c600");

	readonly smallDarkGreenSvgMarker = this.createSvgMarker(false, "#006700");
	readonly largeDarkGreenSvgMarker = this.createSvgMarker(true, "#006700");

	readonly smallGraySvgMarker = this.createSvgMarker(false, "#666666");

	mapLoading = false;
	mapReady = false;
	delayedMapLoadHandle: number;

	mapShowOtherBusinesses: boolean = true;

	currentStore: IMapStore;
	currentStoreMarker: google.maps.Marker;

	currentMall: IMapMall;
	currentMallMarker: google.maps.Marker;
	mallPrio: Priority;
	savingMallPrio: boolean;

	currentPsl: IMapPotentialStoreLocation;
	currentPslMarker: google.maps.Marker;
	potentialStoreLocationId: number;
	potentialStoreLocationName: string;
	potentialStoreLocationPrio: Priority;
	savingPsl: boolean;

	infoWindow: google.maps.InfoWindow;
	prioItemNotSet: Priority = { prio: 0, description: "(Not prioritized)" };
	prioItems: Priority[] = [{ prio: 1, description: "Priority 1" }, { prio: 2, description: "Priority 2" }, { prio: 3, description: "Priority 3" }];

	google: typeof google;
	map: google.maps.Map;
	mainMarkers: google.maps.Marker[] = [];
	mallMarkers: google.maps.Marker[] = [];
	prioritizedMallMarkers: google.maps.Marker[] = [];
	pslMarkers: google.maps.Marker[] = [];
	mainMarkerClusterer: MarkerClusterer;
	mallMarkerClusterer: MarkerClusterer;
	pslMarkerClusterer: MarkerClusterer;

	@Input() storeId: number;
	@Input() storeFocus: boolean;
	@Input() latitude: number;
	@Input() longitude: number;
	@Input() loadStores: boolean;
	@Input() mapStores: IMapStore[];
	@Input() mapMalls: IMapMall[];
	@Input() mapPotentialStoreLocations: IMapPotentialStoreLocation[];
	@Input() enableEditPotentialStores: boolean = true;

	@ViewChild("mapStyleSelector") mapStyleSelectorDiv: ElementRef;
	@ViewChild("storeInfo") storeInfoDiv: ElementRef;
	@ViewChild("newPotentialStoreLocation") newPotentialStoreLocationDiv: ElementRef;
	@ViewChild("existingPotentialStoreLocation") existingPotentialStoreLocationDiv: ElementRef;
	@ViewChild("mallInfo") mallInfoDiv: ElementRef;
		
	ngOnInit() {

		this.potentialStoreLocationPrio = this.prioItems[0];

		if(this.loadStores) {
			this.apiService.map.getStores().then(m => {
				this.mapStores = m;

				if(!this.storeFocus || this.storeId) {
					this.initMap();
				}
			});
		}
	}
	ngOnChanges(changes: SimpleChanges) {
		//console.log("mainMap ngOnChanges", changes);

		for(const propName in changes) {
			const changedProp = changes[propName];
			if(propName === "storeId" && changedProp.currentValue) {

				if(this.mapStores) {
					this.initMap();
				}

			} else if(propName === "mapStores" && !this.loadStores && this.mapStores) {
				if(this.delayedMapLoadHandle) {
					window.clearTimeout(this.delayedMapLoadHandle);
				}
				if(this.map) {
					this.initMarkers();

				} else {
					this.delayedMapLoadHandle = window.setTimeout(() => this.initMap(), 200);
				}			
			}
		}
	}

	toggleShowOtherBusinesses() {
		this.mapShowOtherBusinesses = !this.mapShowOtherBusinesses;

		this.map.setOptions({
			styles: [{
				featureType: "poi.business",
				stylers: [{ visibility: this.mapShowOtherBusinesses ? "on": "off" }],
			}]
		});
	}

	async createPotentialStoreLocation() {

		this.savingPsl = true;

		const position = this.infoWindow.getPosition();

		const psl = await (this.potentialStoreLocationClient.createPotentialStoreLocation(this.potentialStoreLocationName, position.lat(), position.lng(), this.potentialStoreLocationPrio.prio).toPromise());

		const marker = this.createPslMarker(psl.id, this.potentialStoreLocationName, position.lat(), position.lng(), psl.priority);
		this.pslMarkerClusterer.addMarker(marker, false);

		this.savingPsl = false;
		this.potentialStoreLocationName = "";
		this.potentialStoreLocationPrio = this.prioItems[0];

		if(this.infoWindow) {
			this.infoWindow.close();
		}
	}
	async editPotentialStoreLocation() {

		this.savingPsl = true;

		const psl = await (this.potentialStoreLocationClient.editPotentialStoreLocation(this.potentialStoreLocationId, this.potentialStoreLocationName, this.potentialStoreLocationPrio.prio).toPromise());

		const label = this.currentPslMarker.getLabel() as google.maps.MarkerLabel;
		label.text = this.getPslOrMallMarkerLabelText(psl.name, psl.priority);
		this.currentPslMarker.setLabel(label);
		this.currentPslMarker.setIcon(this.getPslOrMallMarkerIcon(psl.priority));

		(this.currentPslMarker as any).psl = psl;
		
		this.savingPsl = false;
		this.potentialStoreLocationId = 0;
		this.potentialStoreLocationName = "";
		this.potentialStoreLocationPrio = this.prioItems[0];

		if(this.infoWindow) {
			this.infoWindow.close();
		}
	}
	async deletePotentialStoreLocation() {

		this.savingPsl = true;

		await (this.potentialStoreLocationClient.deletePotentialStoreLocation(this.potentialStoreLocationId).toPromise());

		this.pslMarkerClusterer.removeMarker(this.currentPslMarker);
		this.currentPslMarker.setMap(null);

		this.pslMarkers.forEach((item, index) => {
			if(item === this.currentPslMarker) { this.pslMarkers.splice(index, 1); }
		});

		this.savingPsl = false;
		this.potentialStoreLocationId = 0;
		this.potentialStoreLocationName = "";
		this.potentialStoreLocationPrio = this.prioItems[0];

		if(this.infoWindow) {
			this.infoWindow.close();
		}
	}
	getPslOrMallMarkerLabelText(name: string, priority: number) {
		if(priority) {
			return `${name} (${priority})`;
		}

		return name;
	}
	getPslOrMallMarkerIcon(priority: number) {

		let svgMarker = this.smallPinkSvgMarker;

		switch(priority) {
			case 1:
				svgMarker = this.smallGreenSvgMarker;
				break;

			case 2:
				svgMarker = this.smallMidGreenSvgMarker;
				break;

			case 3:
				svgMarker = this.smallDarkGreenSvgMarker;
				break;
		}

		return {
			url: `data:image/svg+xml;base64,${svgMarker}`,
			scaledSize: new google.maps.Size(45, 45),
		};
	}
	createPslMarker(id: number, name: string, lat: number, long: number, priority: number) {

		const marker = new google.maps.Marker({
			position: { lat: lat, lng: long },
			map: this.map,
			title: name,
			icon: this.getPslOrMallMarkerIcon(priority),
			label: {
				text: this.getPslOrMallMarkerLabelText(name, priority),
				className: "marker-label",
			},
			clickable: true,
		});

		const psl = {
			id,
			name,
			lat,
			long,
			priority
		} as IMapPotentialStoreLocation;

		marker.addListener("click", () => {

			this.currentPsl = psl;
			this.currentPslMarker = marker;
			this.potentialStoreLocationId = psl.id;
			this.potentialStoreLocationName = psl.name;
			this.potentialStoreLocationPrio = this.prioItems.find(p => p.prio === psl.priority);

			this.infoWindow.setContent(this.existingPotentialStoreLocationDiv.nativeElement);
			this.infoWindow.open({
				anchor: marker,
				map: this.map
			});
		});

		(marker as any).psl = psl;

		return marker;
	}
		
	async saveMallPriority() {

		this.savingMallPrio = true;
		const originalPrio = this.currentMall.priority;

		await this.mallMetadataClient.saveMallMetadata(this.currentMall.mallId, this.mallPrio ? this.mallPrio.prio : 0).toPromise();
		const label = this.currentMallMarker.getLabel() as google.maps.MarkerLabel;
		label.text = this.getPslOrMallMarkerLabelText(this.currentMall.name, this.mallPrio ? this.mallPrio.prio : 0);
		this.currentMallMarker.setLabel(label);
		this.currentMallMarker.setIcon(this.getPslOrMallMarkerIcon(this.mallPrio ? this.mallPrio.prio : 0));

		if(!originalPrio && this.mallPrio.prio) {
			this.mallMarkers.forEach((item, index) => {
				if(item === this.currentMallMarker) { this.mallMarkers.splice(index, 1); }
			});
			this.prioritizedMallMarkers.push(this.currentMallMarker);

			this.mallMarkerClusterer.removeMarker(this.currentMallMarker);
			this.currentMallMarker.setMap(this.map);
			this.pslMarkerClusterer.addMarker(this.currentMallMarker);

		} else if(originalPrio && !this.mallPrio.prio) {
			this.prioritizedMallMarkers.forEach((item, index) => {
				if(item === this.currentMallMarker) { this.prioritizedMallMarkers.splice(index, 1); }
			});
			this.mallMarkers.push(this.currentMallMarker);

			this.pslMarkerClusterer.removeMarker(this.currentMallMarker);
			this.currentMallMarker.setMap(this.map);
			this.mallMarkerClusterer.addMarker(this.currentMallMarker);
		}

		if(this.mallPrio) {
			this.currentMall.priority = this.mallPrio.prio;

		} else {
			this.currentMall.priority = this.prioItemNotSet.prio;
		}

		this.closeInfoWindow();
		this.savingMallPrio = false;
	}
	createMallMarker(mall: IMapMall) {

		const marker = new this.google.maps.Marker({
			position: { lat: mall.lat, lng: mall.long },
			map: this.map,
			title: mall.name,
			icon: this.getPslOrMallMarkerIcon(mall.priority),
			label: {
				text: this.getPslOrMallMarkerLabelText(mall.name, mall.priority),
				className: "marker-label",
			}
		});

		marker.addListener("click", () => {
			this.currentMall = mall;
			this.currentMallMarker = marker;
			this.mallPrio = this.prioItems.find(p => p.prio === mall.priority) || this.prioItemNotSet;

			this.infoWindow.setContent(this.mallInfoDiv.nativeElement);
			this.infoWindow.open({
				anchor: marker,
				map: this.map
			});
		});

		(marker as any).mall = mall;

		return marker;
	}

	closeInfoWindow() {
		if(this.infoWindow) {
			this.infoWindow.close();
		}
	}
	zoomToMarker(marker: google.maps.Marker) {
		this.map.setCenter(marker.getPosition());

		let zoomLevel = 13;
		const currentZoomLevel = this.map.getZoom();
		if(currentZoomLevel >= 13) {
			zoomLevel = currentZoomLevel + 2;
		}
		if(zoomLevel >= 22) {
			zoomLevel = 22;
		}

		this.map.setZoom(zoomLevel);
		this.closeInfoWindow();
		event.cancelBubble = true;
		event.preventDefault();
	};

	initMainMarkers() {
		if(!this.google || !this.map) {
			return;
		}

		if(this.mainMarkerClusterer) {
			this.mainMarkerClusterer.clearMarkers();
		}
		if(this.mainMarkers) {
			for(let i = 0; i < this.mainMarkers.length; i++) {
				this.mainMarkers[i].setMap(null);
			}

			this.mainMarkers = [];
		}

		this.mainMarkers = this.mapStores && this.mapStores.length ? this.mapStores.filter(ms => ms.lat && ms.long).map(ms => {

			const isCurrentStore = this.storeId && ms.storeId === this.storeId;

			let svgIcon = this.smallBlueSvgMarker;
			if(ms.storeProjected) {
				svgIcon = this.smallBlueSvgMarker;
			}

			if(isCurrentStore) {
				svgIcon = this.smallRedSvgMarker;
			}

			const marker = new this.google.maps.Marker({
				position: { lat: ms.lat, lng: ms.long },
				map: this.map,
				title: ms.popularName,
				icon: {
					url: `data:image/svg+xml;base64,${svgIcon}`,
					scaledSize: new this.google.maps.Size(45, 45),
				},
				label: {
					text: ms.popularName,
					className: "marker-label marker-label-store",
					fontWeight: isCurrentStore ? "bold" : null
				},
				zIndex: Number(this.google.maps.Marker.MAX_ZINDEX) + 100
			});

			marker.addListener("click", () => {
				this.currentStore = ms;
				this.currentStoreMarker = marker;

				this.infoWindow.setContent(this.storeInfoDiv.nativeElement);
				this.infoWindow.open({
					anchor: marker,
					map: this.map
				});
			});

			(marker as any).store = ms;

			return marker;
		}) : [];

		const mainMarkerClustererRender = (cluster: Cluster, stats: ClusterStats) => {

			return new this.google.maps.Marker({
				position: cluster.position,
				icon: {
					url: `data:image/svg+xml;base64,${this.largeBlueSvgMarker}`,
					scaledSize: new this.google.maps.Size(45, 45),
				},
				label: {
					text: String(cluster.count),
					color: "rgba(255,255,255,0.9)",
					fontSize: "12px",
				},
				title: `Cluster of ${cluster.count} stores`,
				// adjust zIndex to be above other markers
				zIndex: Number(this.google.maps.Marker.MAX_ZINDEX) + cluster.count - 1,
			});
		};

		if(this.mainMarkerClusterer) {
			this.mainMarkerClusterer.clearMarkers(false);
			this.mainMarkerClusterer.addMarkers(this.mainMarkers);

		} else {
			this.mainMarkerClusterer = new MarkerClusterer({ map: this.map, markers: this.mainMarkers, renderer: { render: mainMarkerClustererRender } });
		}
	}
	initMallMarkers() {

		if(!this.google || !this.map) {
			return;
		}

		if(this.mallMarkerClusterer) {
			this.mallMarkerClusterer.clearMarkers();
		}
		if(this.mallMarkers) {
			for(let i = 0; i < this.mallMarkers.length; i++) {
				this.mallMarkers[i].setMap(null);
			}

			this.mallMarkers = [];
		}

		if(this.mapMalls) {
			this.mallMarkers = this.mapMalls.filter(mm => mm.lat && mm.long && !mm.priority).map(mm => this.createMallMarker(mm));
		}

		const mallMarkerClustererRender = (cluster: Cluster, stats: ClusterStats) => {

			return new this.google.maps.Marker({
				position: cluster.position,
				icon: {
					url: `data:image/svg+xml;base64,${this.largePinkSvgMarker}`,
					scaledSize: new this.google.maps.Size(45, 45),
				},
				label: {
					text: String(cluster.count),
					color: "rgba(0,0,0,0.9)",
					fontSize: "12px",
				},
				title: `Cluster of ${cluster.count} malls and street locations`,
				// adjust zIndex to be above other markers
				zIndex: Number(this.google.maps.Marker.MAX_ZINDEX) + cluster.count - 1,
			});
		};

		if(this.mallMarkerClusterer) {
			this.mallMarkerClusterer.clearMarkers(false);
			this.mallMarkerClusterer.addMarkers(this.mallMarkers);

		} else {
			this.mallMarkerClusterer = new MarkerClusterer({ map: this.map, markers: this.mallMarkers, renderer: { render: mallMarkerClustererRender }, algorithm: new GridAlgorithm({}) });
		}
	}
	initPslMarkers() {
		if(!this.google || !this.map) {
			return;
		}

		if(this.pslMarkerClusterer) {
			this.pslMarkerClusterer.clearMarkers();
		}
		if(this.pslMarkers) {
			for(let i = 0; i < this.pslMarkers.length; i++) {
				this.pslMarkers[i].setMap(null);
			}

			this.pslMarkers = [];
		}
		if(this.prioritizedMallMarkers) {
			for(let i = 0; i < this.prioritizedMallMarkers.length; i++) {
				this.prioritizedMallMarkers[i].setMap(null);
			}

			this.prioritizedMallMarkers = [];
		}

		if(this.mapPotentialStoreLocations && this.mapPotentialStoreLocations.length > 0) {
			this.pslMarkers = this.mapPotentialStoreLocations.map(psl => this.createPslMarker(
				psl.id,
				psl.name,
				psl.lat,
				psl.long,
				psl.priority
			));
		}

		if(this.mapMalls) {
			this.prioritizedMallMarkers = this.mapMalls.filter(mm => mm.lat && mm.long && mm.priority).map(mm => this.createMallMarker(mm));
		}

		const pslClusterRender = (cluster: Cluster, stats: ClusterStats) => {

			return new this.google.maps.Marker({
				position: cluster.position,
				icon: {
					url: `data:image/svg+xml;base64,${this.largeGreenSvgMarker}`,
					scaledSize: new this.google.maps.Size(45, 45),
				},
				label: {
					text: String(cluster.count),
					color: "rgba(255,255,255,0.9)",
					fontSize: "12px",
				},
				title: `Cluster of ${cluster.count} potential store locations`,
				// adjust zIndex to be above other markers
				zIndex: Number(this.google.maps.Marker.MAX_ZINDEX) + cluster.count - 1,
			});
		};

		const allMarkers = [...this.pslMarkers, ...this.prioritizedMallMarkers];

		if(this.pslMarkerClusterer) {
			this.pslMarkerClusterer.clearMarkers(false);
			this.pslMarkerClusterer.addMarkers(allMarkers);

		} else {
			if(this.pslMarkers) {
				this.pslMarkerClusterer = new MarkerClusterer({ map: this.map, markers: allMarkers, renderer: { render: pslClusterRender }, algorithm: new GridAlgorithm({}) });
			}
		}
	}
	initMarkers() {

		if(!this.google || !this.map) {
			return;
		}

		this.initMainMarkers();
		this.initMallMarkers();
		this.initPslMarkers();
	}

	initMap() {

		if(this.mapLoading) {
			return;
		}

		this.mapLoading = true;
		console.log("initMap!");
		const loader = new Loader({
			apiKey: "AIzaSyDH7VoFCAsmmxFppKEJxY2vyS9S6UypCcc",
			version: "weekly",
			libraries: ["places"]
		});

		const mapOptions: google.maps.MapOptions = {
			center: {
				lat: 0,
				lng: 0
			},
			zoom: 5,
			gestureHandling: "greedy",
			styles: [
				//{
				//	featureType: "poi",
				//	stylers: [{ visibility: "off" }],
				//},
				//{
				//	featureType: "transit",
				//	elementType: "labels.icon",
				//	stylers: [{ visibility: "off" }],
				//}
			]

		};

		loader
			.load()
			.then((google) => {

				this.google = google;

				this.infoWindow = new google.maps.InfoWindow({
					content: "",
					ariaLabel: "",
				});

				this.map = new google.maps.Map(document.getElementById("map"), mapOptions);

				this.map.addListener("rightclick", (e: google.maps.MapMouseEvent) => {

					if(this.enableEditPotentialStores) {
						this.infoWindow.setContent(this.newPotentialStoreLocationDiv.nativeElement);
						this.infoWindow.setPosition(e.latLng);
						this.infoWindow.open({ map: this.map });
					}
				});

				// Add controls to the map, allowing users to hide/show features.
				this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(this.mapStyleSelectorDiv.nativeElement);

				this.initMarkers();

				let startPosition: google.maps.LatLngLiteral;
				if(this.storeId) {
					const currentStore = this.mapStores.find(s => s.storeId === this.storeId);
					if(currentStore) {
						startPosition = { lat: currentStore.lat, lng: currentStore.long };
					}
				}

				if(startPosition) {
					//console.log("Setting center to startPosition: ", startPosition);
					this.map.setCenter(startPosition);
					this.map.setZoom(12);

				} else {
					if(this.latitude && this.longitude) {
						//console.log("Setting center to lat/long: ", this.latitude, this.longitude);
						this.map.setCenter({ lat: this.latitude, lng: this.longitude });

					} else if(this.mainMarkers.length) {
						//console.log("Setting center to marker 0: ", this.mainMarkers[0].getPosition());
						this.map.setCenter(this.mainMarkers[0].getPosition());

					} else {
						// North europe
						this.map.setCenter({ lat: 52.755329350412644, lng: 11.054219116361926 });
					}
				}

				this.mapReady = true;
				this.mapLoading = false;


				//console.log("mapReady = true", this.mapReady);
			})
			.catch(e => {
				// do something
			});
	}


	list() { }
	grid() { }

	createSvgMarker(large: boolean, color: string) {

		if(large) {
			return window.btoa(
				`<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
					<circle cx="120" cy="120" opacity=".6" r="70" />
					<circle cx="120" cy="120" opacity=".3" r="90" />
					<circle cx="120" cy="120" opacity=".2" r="110" />
				</svg>`
			);
		}

		return window.btoa(
			`<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
				<circle cx="120" cy="120" opacity=".9" r="30" />
				<circle cx="120" cy="120" opacity=".5" r="50" />
				<circle cx="120" cy="120" opacity=".6" fill="none" stroke="#000" stroke-width="5" r="50" />
				<circle cx="120" cy="120" opacity=".2" fill="none" stroke="#000" stroke-width="10" r="65" />
			</svg>`
		);
	}
}

							
class Priority {
	prio: number;
	description: string;
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular2-router-loader/src!./TypeScript/shared/map/main-map.component.ts